import { REPORT_SPECS } from "../modules/reports/catalog";
import { canOpenCreatePlan } from "../api/planBuilder";
import { canPropose } from "../api/proposal";
import { canKeepPriceListDocs } from "../api/priceListDocs";

/**
 * The left navigation, as GROUPS with a submenu each: the information
 * architecture the owner approved on the Welcome Page canvas (2026-09-14).
 *
 * The order is the brief's: Plans, Users, Billing, Devices, Reports,
 * Provisioning, Portal Themes, Member Roster, Legal & Terms. A group whose
 * screens are all switched off is not shown at all -- the same rule the routes
 * follow (no flag, NO ROUTE), so Billing, Devices and Provisioning appear the
 * day their first screen ships and not before.
 */
export interface NavLeaf {
  to: string;
  label: string;
  /** The screen really calls a model (radagent): the menu marks it with the AI icon. */
  ai?: boolean;
  /**
   * A screen that still lives in the PHP portal, opened there in a new tab.
   * The nav shows every area the brief names; where AnkhOS has no screen yet
   * the entry goes to the one that exists rather than to nothing.
   */
  external?: boolean;
  /**
   * A provider entry: choosing it ENTERS that namespace (auth/scope.ts) and
   * goes home, rather than navigating to a route. Only the Providers group
   * carries these.
   */
  switchTo?: string;
  /**
   * A category's own screens (owner 2026-10-25: Reports grew to twenty, so the
   * sidebar shows four categories and each opens a page of its reports). The
   * sidebar draws only the category; the menu search and the category page
   * read these.
   */
  sub?: NavLeaf[];
  /**
   * Shown greyed and not clickable: an area the owner wants visible in the
   * menu but whose screen is not built yet (owner 2026-10-26: Provisioning,
   * Settings › BNG and TR069). The menu search leaves it out.
   */
  disabled?: boolean;
}

export type NavGroupKey =
  | "platform"
  | "providers"
  | "plans"
  | "promotions"
  | "users"
  | "payments"
  | "sms"
  | "tools"
  | "billing"
  | "devices"
  | "reports"
  | "provisioning"
  | "themes"
  | "roster"
  | "legal"
  | "settings";

export interface NavGroup {
  key: NavGroupKey;
  label: string;
  items: NavLeaf[];
}

const OPERATOR_ROLES = ["superadmin", "ossadmin", "bssadmin", "operator", "reseller", "support", "manager", "billing", "technical", "viewer"];

/**
 * The roles that act as the operator itself in its own namespace: superadmin,
 * the operator, and the operator's MANAGER -- its senior staff, shown as
 * "Admin" (owner 2026-10-18; radaccess v1.121 derives manager's grants from
 * Operator's, less handing out logins). Every "superadmin and Operator"
 * predicate below admits a manager.
 */
const OWNERS = ["superadmin", "operator", "manager"];

/**
 * Operator STAFF below the manager (owner 2026-10-18): billing, support,
 * technical, viewer. A login holding only these sees the screens its job needs
 * and none of the operator's own -- profile, legal pages, portal themes,
 * Ankh's billing desk -- that the gateway refuses it.
 */
const STAFF_ROLES = ["billing", "support", "technical", "viewer"];

/** A login that holds only staff roles (no operator, manager or platform role). DISPLAY ONLY. */
export function isStaffOnly(roles: string[]): boolean {
  const lower = roles.map((r) => r.toLowerCase());
  return lower.some((r) => STAFF_ROLES.includes(r)) && lower.every((r) => STAFF_ROLES.includes(r));
}

function holdsAny(roles: string[], allowed: readonly string[]): boolean {
  return roles.some((r) => allowed.includes(r.toLowerCase()));
}

/**
 * The provider's own customer ledger (Issued invoices) and recording a payment
 * against it (owner 2026-10-25; radaccess v1.159 tenantledger:read and
 * directpayment:write). ⚠️ DISPLAY ONLY: radaccess decides.
 */
export function canReadTenantLedger(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin", "operator", "manager", "billing", "platform_admin", "platform_billing", "platform_viewer"]);
}

export function canRecordPayment(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin", "operator", "manager", "billing"]);
}

/**
 * Upstream agreements (owner OB-14, 2026-10-28): Owner, Admin and Billing
 * record them; superadmin reads (radaccess RBAC "upstream").
 */
export function canReadUpstream(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin", "operator", "manager", "billing"]);
}

/**
 * Whose name is on internet invoices (owner OB-12, 2026-10-28): Owner and
 * Admin ask and consent; Billing and superadmin read (radaccess RBAC "invoicename").
 */
export function canReadInvoiceName(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin", "operator", "manager", "billing"]);
}

/**
 * The invoice logo (owner 2026-10-29): Owner and Admin upload it; Billing and
 * superadmin read (radaccess v1.211, RBAC "invoicelogo"). DISPLAY ONLY.
 */
export function canReadInvoiceLogo(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin", "operator", "manager", "billing"]);
}

export function canEditInvoiceLogo(roles: string[]): boolean {
  return holdsAny(roles, ["operator", "manager"]);
}

/**
 * A login that holds `sponsor` and nothing that reaches a provider's screens.
 *
 * ⚠️ DISPLAY ONLY. radaccess and radcustomer decide what a sponsor may do;
 * this decides which links are worth showing it.
 */
export function isSponsorOnly(roles: string[]): boolean {
  const lower = roles.map((r) => r.toLowerCase());
  return lower.includes("sponsor") && !lower.some((r) => OPERATOR_ROLES.includes(r));
}

/**
 * A login that signed up and is not approved yet: `applicant` and nothing that
 * reaches a provider's screens. It is asked what kind of operator it is.
 *
 * ⚠️ DISPLAY ONLY, like isSponsorOnly: radaccess decides what it may do.
 */
export function isApplicantOnly(roles: string[]): boolean {
  const lower = roles.map((r) => r.toLowerCase());
  return lower.includes("applicant") && !lower.some((r) => OPERATOR_ROLES.includes(r));
}

/**
 * May this login EDIT a provider's legal identity (legal name, PAN, GSTIN,
 * GST status, registered address, licence)? radaccess keeps every write on
 * `operator:*`, superadmin only. An Operator holds `operatoridentity:read`
 * (radaccess feat/operator-identity-read) and sees them read-only (owner
 * decision 2026-09-19).
 *
 * ⚠️ DISPLAY ONLY, no wider than the server: change it in the same change
 * that changes the grant.
 */
export function canEditProviderIdentity(roles: string[]): boolean {
  return roles.some((r) => r.toLowerCase() === "superadmin");
}

/**
 * May this login read what Ankh billed its provider (invoices and receipts)?
 * radaccess v1.68 grants `ankhbilling:read` to superadmin and Operator only.
 *
 * ⚠️ DISPLAY ONLY, and deliberately no wider than the server: a link the
 * gateway refuses is worse than no link. Change this in the same change that
 * changes the grant (radaccess app/handler/rbac.go).
 */
/**
 * May this login read a provider's price list (its components and price
 * rules)? radaccess v1.81 grants `pricelist:read` to superadmin and Operator
 * only, read-only (owner 2026-09-28).
 *
 * ⚠️ DISPLAY ONLY, and no wider than the server. Change it with the grant
 * (radaccess app/handler/rbac.go).
 */
export function canReadPriceList(roles: string[]): boolean {
  return roles.some((r) => OWNERS.includes(r.toLowerCase()));
}

export const PRICE_LIST_PATH = "/plans/prices";

/** Plans › Create plan (C5): shown to the gateway's plan:write holders. */
export const CREATE_PLAN_PATH = "/plans/create";
export const PROPOSE_PATH = "/plans/propose";
export const PROMPT_PLAN_PATH = "/plans/prompt";
/** Plans › Price-list documents (owner 2026-10-29): every rate card and price list kept. */
export const PRICE_LIST_DOCS_PATH = "/plans/documents";
export const ON_SALE_PATH = "/plans/on-sale";
/** Promotions › Festive and › Promotion codes (owner 2026-10-14: a menu of its own). */
export const PROMOTIONS_PATH = "/promotions/festive";
export const PROMO_CODES_PATH = "/promotions/codes";
/** Promotions › Report (owner 2026-10-15): who used which promotion, and what it gave. */
export const PROMO_REPORT_PATH = "/promotions/report";

/**
 * Who sees Plans › Promotions (a festive promotional price on chosen plans,
 * design §9.17). DISPLAY ONLY: radaccess's promotion:write decides.
 * superadmin, bssadmin, Operator -- never support staff.
 */
export function canRunPromotions(roles: string[]): boolean {
  return roles.some((r) => [...OWNERS, "bssadmin"].includes(r.toLowerCase()));
}

/**
 * Who sees Plans › On sale (close a plan to new sales, reopen it). DISPLAY
 * ONLY: radaccess's radgroup:write decides. Never support staff (owner
 * 2026-10-11).
 */
export function canClosePlans(roles: string[]): boolean {
  return roles.some((r) => [...OWNERS, "bssadmin"].includes(r.toLowerCase()));
}

/** Plans › Components (C4): the same reads and the same grant as the price list. */
export const COMPONENTS_PATH = "/plans/components";

export function canReadAnkhInvoices(roles: string[]): boolean {
  return roles.some((r) => OWNERS.includes(r.toLowerCase()));
}

/**
 * May this login run organisations (create, approve plans, set limits, invite
 * sponsors)? radaccess v1.65 grants `rosterorg` to superadmin and Operator
 * only -- never bssadmin, never a sponsor (owner, design §13.1).
 *
 * ⚠️ DISPLAY ONLY, and no wider than the server. Change it with the grant
 * (radaccess app/handler/rbac.go).
 */
export function canManageOrganisations(roles: string[]): boolean {
  return roles.some((r) => OWNERS.includes(r.toLowerCase()));
}

/**
 * May this login add and suspend its own sub-operators? radaccess grants
 * `suboperator:read|write|suspend` to superadmin and Operator only
 * (feat/suboperator, owner mandate 2026-09-19).
 *
 * ⚠️ DISPLAY ONLY, and no wider than the server. Change this in the same change
 * that changes the grant (radaccess app/handler/rbac.go).
 */
export function canManageSubOperators(roles: string[]): boolean {
  return roles.some((r) => OWNERS.includes(r.toLowerCase()));
}

/**
 * Pin plans: radaccess grants `pinplan:read|write` to superadmin and Operator
 * only (feat/pin-plan, owner 2026-09-19). A parent publishes there; a PIN
 * sub-operator reads what it is offered on the same screen.
 *
 * ⚠️ DISPLAY ONLY, and no wider than the server (radaccess app/handler/rbac.go).
 */
/**
 * May this login invite, re-role and disable the operator's STAFF and the
 * owners of its sub-operators? radaccess v1.119 grants `operatorlogin:read|write`
 * to superadmin and Operator only -- no staff role (billing, support,
 * technical, viewer) may hand out a login.
 *
 * ⚠️ DISPLAY ONLY, and no wider than the server (radaccess app/handler/rbac.go).
 */
export function canManageStaff(roles: string[]): boolean {
  // Not OWNERS: a manager does not hand out logins (radaccess managerGrants).
  return holdsAny(roles, ["superadmin", "operator"]);
}

/**
 * One subscriber's own RADIUS attributes (owner 2026-10-29): superadmin and the
 * operator's Owner ONLY -- not a manager (Admin) or any staff role
 * (radaccess subscriberradius:*, excluded from managerGrants). DISPLAY ONLY.
 */
export function canEditRadiusAttrs(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin", "operator"]);
}

/** Settings › Staff (owner, 2026-10-18). Not under /settings/:slug, which is the preferences pages. */
export const STAFF_PATH = "/staff";

export function canUsePinPlans(roles: string[]): boolean {
  return roles.some((r) => OWNERS.includes(r.toLowerCase()));
}

export const PIN_PLANS_PATH = "/providers/pins";

/**
 * Pins: radaccess grants `pinorder:read|write|approve` and `pin:read|revoke`
 * to superadmin and Operator only (feat/pin-orders, owner 2026-09-19, step 3).
 * The same entry serves both sides -- a PIN sub-operator buys there, its
 * parent records payment, approves and revokes.
 *
 * ⚠️ DISPLAY ONLY, and no wider than the server (radaccess app/handler/rbac.go).
 */
export function canUsePinOrders(roles: string[]): boolean {
  return roles.some((r) => OWNERS.includes(r.toLowerCase()));
}

export const PIN_ORDERS_PATH = "/providers/pinorders";

/**
 * Disable subscriber: radaccess grants `subscriberdisable:write` to superadmin
 * and Operator only (owner 2026-10-14); raduser and radcustomer decide again.
 *
 * ⚠️ DISPLAY ONLY, and no wider than the server (radaccess app/handler/rbac.go).
 */
export function canDisableSubscriber(roles: string[]): boolean {
  // Support holds subscriberdisable:write (radaccess v1.119).
  return holdsAny(roles, [...OWNERS, "support"]);
}

export const SUBSCRIBER_PROFILES_PATH = "/users/profiles";

/**
 * Recharge: radaccess grants `subscriberrecharge:read|write` to superadmin and
 * Operator only (owner 2026-10-10). Every other role keeps the PHP page.
 *
 * ⚠️ DISPLAY ONLY, and no wider than the server (radaccess app/handler/rbac.go).
 */
export function canRecharge(roles: string[]): boolean {
  // billing holds subscriberrecharge, invoicepref:read and cart:read (radaccess v1.121).
  return holdsAny(roles, [...OWNERS, "billing"]);
}

/**
 * A subscriber's receipts: radaccess subscriberreceipt:read -- the owners,
 * billing and viewer. DISPLAY ONLY.
 */
export function canReadReceipts(roles: string[]): boolean {
  return holdsAny(roles, [...OWNERS, "billing", "viewer"]);
}

/** Installations and equipment on a subscriber: radaccess installation:*, owners only. */
export function canManageInstallations(roles: string[]): boolean {
  return holdsAny(roles, OWNERS);
}

export const RECHARGE_PATH = "/billing/recharge";

/** Billing › Customers (Ankh): io.ankhos's own customers and invoices (owner 2026-10-23). */
export const ANKH_CUSTOMERS_PATH = "/billing/customers";
/** Billing › Upstream supplier (owner OB-9, 2026-10-28; radaccess v1.193). */
export const UPSTREAM_PATH = "/billing/upstream";

/** Billing › Name on invoices (owner OB-12, 2026-10-28; radaccess v1.198). */
export const INVOICE_NAME_PATH = "/billing/invoice-name";

/** Platform › Tenants, invitations, promotions (owner 2026-10-24). */
export const PLATFORM_PATH = "/platform";
/** The platform owner's namespace: the only one with Platform and Customers (Ankh). */
export const PLATFORM_NAMESPACE = "io.ankhos";

/**
 * At io.ankhos the console is the PLATFORM's (owner 2026-10-24: "io.ankhos is
 * selling a platform ... nothing to do with ISP operation"): these groups only.
 * The ISP menus come back the moment the owner enters a tenant.
 * Owner 2026-10-24: no Providers (operators are the platform's customers, listed
 * under Platform, not sub-operators), and Reports holds the SMS log alone.
 */
export const PLATFORM_GROUPS: readonly NavGroupKey[] = ["platform", "plans", "billing", "payments", "sms", "reports", "legal", "settings"];
/**
 * io.ankhos's own staff (owner 2026-10-24, radaccess v1.154): granted only at
 * io.ankhos, each on platform resources only. ⚠️ DISPLAY ONLY, and no wider
 * than the server (radaccess rbac.go): platformtenant, ankhcustomer.
 */
export const PLATFORM_STAFF_ROLES = ["platform_admin", "platform_billing", "platform_onboarding", "platform_viewer"];

/** A login whose roles are all platform staff roles (not the owner). */
/**
 * Does this login land on the PLATFORM Welcome Page (owner 2026-10-24)? The
 * same gate that gives it the Platform menu: at io.ankhos itself (not inside a
 * provider), platform.tenants on, superadmin or platform staff.
 */
export function showsPlatformWelcome(
  flagEnabled: Flag,
  roles: string[],
  namespace: string | null | undefined,
  insideProvider: boolean,
): boolean {
  return (
    !insideProvider &&
    namespace === PLATFORM_NAMESPACE &&
    flagEnabled("platform.tenants") &&
    (canEditProviderIdentity(roles) || isPlatformStaff(roles))
  );
}

export function isPlatformStaff(roles: string[]): boolean {
  const lower = roles.map((r) => r.toLowerCase());
  return lower.some((r) => PLATFORM_STAFF_ROLES.includes(r)) && lower.every((r) => PLATFORM_STAFF_ROLES.includes(r));
}

/** Platform › Tenants & invitations: platformtenant:read. */
export function canReadPlatformTenants(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin", ...PLATFORM_STAFF_ROLES]);
}

/** Invite, resend, revoke, suspend, promotions: platformtenant:write. */
export function canWritePlatformTenants(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin", "platform_admin", "platform_onboarding"]);
}

/** Billing › Customers (Ankh): ankhcustomer:read. */
export function canReadAnkhCustomers(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin", "platform_admin", "platform_billing", "platform_viewer"]);
}

/**
 * Reports › SMS log (owner 2026-10-25, "allow Support"): the operator, its
 * Admin, Support, and of the platform staff platform_admin alone. Billing,
 * viewer and technical staff do not see it, nor do reseller / bssadmin /
 * ossadmin ("no allow"); radreportsapi v0.1.39 refuses them (smsLogRoles).
 * Support sees the codes masked (radreports smslogcodes.go).
 */
export function canReadSmsLog(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin", "admin", "operator", "manager", "support", "platform_admin"]);
}

/** Open accounts, raise invoices: ankhcustomer:write. */
export function canWriteAnkhCustomers(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin", "platform_admin", "platform_billing"]);
}

/** Platform › Platform plans (owner 2026-10-30): platformplan, superadmin only. DISPLAY ONLY. */
export function canManagePlatformPlans(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin"]);
}

/** Platform › Platform plans: io.ankhos plans billed by a meter. */
export const PLATFORM_PLANS_PATH = "/platform/plans";

/** The test-tenant purge: platformpurge, the owner's alone. */
export function canPurgeTenants(roles: string[]): boolean {
  return holdsAny(roles, ["superadmin"]);
}

/** The only reports the platform reads: what it sent (owner 2026-10-24). */
export const PLATFORM_REPORTS: readonly string[] = ["/reports/sms-log", "/reports/tenant-summary"];

/**
 * Users › Customer lookup (owner 2026-10-18): radaccess subscribersearch:read
 * -- the operator, superadmin and every operator staff role.
 */
export const CUSTOMER_LOOKUP_PATH = "/users/lookup";

export function canSearchSubscribers(roles: string[]): boolean {
  return holdsAny(roles, [...OWNERS, "support", "technical", "billing", "viewer"]);
}

/** Edit profile: radaccess subscriberprofile:write -- the owners and support (v1.123). DISPLAY ONLY. */
export function canEditProfile(roles: string[]): boolean {
  return holdsAny(roles, [...OWNERS, "support"]);
}
/** Send login details / Reset password (radaccess subscriberlogin:write, v1.125): the Edit profile roles. */
export function canSendLoginDetails(roles: string[]): boolean {
  return holdsAny(roles, [...OWNERS, "support"]);
}
/** Users › Disable user and Billing › Print voucher: the Recharge screen's search, one action each (owner 2026-10-15). */
export const DISABLE_USER_PATH = "/users/disable";
export const PRINT_VOUCHER_PATH = "/billing/print-voucher";
/**
 * Tools › User diagnose (owner 2026-10-21, moved from Users) and Users ›
 * Change Device MAC: the same search, one action each. The old
 * /users/diagnose address forwards here.
 */
export const DIAGNOSE_USER_PATH = "/tools/diagnose";
export const LEGACY_DIAGNOSE_USER_PATH = "/users/diagnose";
/** Tools › Test RADIUS login (owner 2026-10-21; radaccess v1.134). */
export const RADIUS_TEST_PATH = "/tools/radius-test";

/** Payment Gateway › Gateway accounts (owner 2026-10-24, radaccess v1.156). */
export const PAYMENT_GATEWAY_PATH = "/payments/gateway";
/** SMS Gateway (owner 2026-10-30): the operator's settings and fund; io.ankhos's view of every fund. */
export const SMS_GATEWAY_PATH = "/sms/gateway";
export const SMS_FUND_PATH = "/sms/fund";
export const SMS_FUNDS_PATH = "/sms/funds";
/** Billing › UPI payment notices (owner 2026-10-24, radaccess v1.156). */
export const UPI_NOTICES_PATH = "/billing/upi-notices";
/** Billing › New connection requests (owner 2026-11-02, public sign-up's wired link). */
export const CONNECTION_REQUESTS_PATH = "/billing/connection-requests";

/**
 * Payment Gateway: radaccess paymentgateway -- superadmin, Operator and its
 * Admin (manager) only, so a subscriber override is the admins' alone (owner
 * 2026-10-24). DISPLAY ONLY.
 */
export function canManagePaymentGateway(roles: string[]): boolean {
  return holdsAny(roles, OWNERS);
}

/** UPI payment notices: radaccess paymentnotice:read -- the owners and every staff role. DISPLAY ONLY. */
export function canReadUpiNotices(roles: string[]): boolean {
  return holdsAny(roles, [...OWNERS, ...STAFF_ROLES]);
}

/** Apply / Reject a UPI notice: radaccess paymentnotice:write|apply -- the owners and billing. DISPLAY ONLY. */
export function canDecideUpiNotices(roles: string[]): boolean {
  return holdsAny(roles, [...OWNERS, "billing"]);
}
export const CHANGE_ONU_PATH = "/devices/change-onu";

/** User diagnose: radaccess subscriberdevice:read -- the owners, support and technical. DISPLAY ONLY. */
export function canDiagnose(roles: string[]): boolean {
  return holdsAny(roles, [...OWNERS, "support", "technical"]);
}

/** Test RADIUS login: radaccess subscriberdevice:read PINNED (the same staff as User diagnose, owner 2026-10-21). DISPLAY ONLY. */
export function canTestRadiusLogin(roles: string[]): boolean {
  return canDiagnose(roles);
}

/** Change Device MAC: radaccess subscriberdevice:write -- the owners and technical. DISPLAY ONLY. */
export function canChangeDeviceMac(roles: string[]): boolean {
  return holdsAny(roles, [...OWNERS, "technical"]);
}

/**
 * The subscriber-list screens (Recharge, Print voucher, User diagnose, Change
 * Device MAC, Disable user) each open for the roles that can do their one job.
 */
export function canOpenSubscriberScreen(mode: "recharge" | "voucher" | "diagnose" | "radiustest" | "onu" | "disable", roles: string[]): boolean {
  switch (mode) {
    case "recharge":
    case "voucher":
      return canRecharge(roles);
    case "diagnose":
      return canDiagnose(roles);
    case "radiustest":
      return canTestRadiusLogin(roles);
    case "onu":
      return canChangeDeviceMac(roles);
    case "disable":
      return canDisableSubscriber(roles);
  }
}

/**
 * Create subscriber: radaccess `subscribercreate:write` is granted to the same
 * two roles as Recharge (owner 2026-10-14). DISPLAY ONLY, as canRecharge.
 */
export function canCreateSubscriber(roles: string[]): boolean {
  // Not billing: subscribercreate:write is the owners' (radaccess v1.121).
  return holdsAny(roles, OWNERS);
}

export const CREATE_SUBSCRIBER_PATH = "/users/create";

/** The Providers submenu's last entry (owner, 2026-09-19). */
export const ADD_PROVIDER_PATH = "/providers/new";

/**
 * The groups a CLOUD TENANT sees (owner decision 2026-09-16): a provider with
 * no subscribers and no network that buys from Ankh. It keeps what it owes
 * Ankh, its legal pages and its settings; everything about plans, subscribers,
 * sub-providers, devices and portals is hidden.
 *
 * ⏳ "Not now, maybe later": the owner wants a per-provider switch that gives
 * one cloud tenant the subscriber screens back. Where that switch lives is
 * still the owner's question, so there is no switch yet.
 */
export const CLOUD_TENANT_GROUPS: readonly NavGroupKey[] = ["billing", "legal", "settings"];

/**
 * Settings a cloud tenant has no use for (owner, 2026-09-18: "in setting bng
 * and tr069 is not require for cloud tenant") -- it runs no access network, so
 * no BNG and no customer-premises devices to manage over TR-069.
 */
// A cloud tenant has no staff to invite (owner 2026-10-18: staff logins are an operator's).
export const CLOUD_TENANT_HIDDEN_SETTINGS: readonly string[] = ["/settings/bng", "/settings/tr069", "/staff"];

type Flag = (name: string) => boolean;

// A report entry exists only if its flag is on -- the nav is derived from the
// same catalogue the routes are, so the two cannot disagree.
function report(flagEnabled: Flag, slug: string, label?: string): NavLeaf[] {
  const spec = REPORT_SPECS.find((r) => r.slug === slug);
  if (!spec || !flagEnabled(spec.flag)) return [];
  return [{ to: `/reports/${spec.slug}`, label: label ?? spec.title }];
}

/**
 * The Reports categories (owner 2026-10-25: "divide into further categories so
 * the list is 4-5 items"). Each is a sidebar entry opening /reports/<key>, a
 * page of its reports; a report sits in exactly one.
 */
export const REPORT_CATEGORIES = [
  { key: "usage", label: "Usage & logins", slugs: ["active-sessions", "user-sessions", "top-usage", "peak-hours", "never-used", "failed-logins"] },
  { key: "subscribers", label: "Subscribers", slugs: ["active-plans", "expiring-plans", "expired-not-renewed", "new-subscribers", "renewals-by-plan", "base-snapshot"] },
  { key: "money", label: "Money", slugs: ["recharge-log", "recharge-log-by-user", "collections", "funds-statement", "payment-failures", "promo-use"] },
  { key: "operations", label: "Operations", slugs: ["sms-log", "staff-activity", "tenant-summary"] },
] as const;

/** At or under this many reports the menu lists them directly: a category of two is a click for nothing. */
export const REPORTS_FLAT_UP_TO = 5;

/** The categories holding at least one report; flattened when there are few. */
function reportCategories(reports: NavLeaf[]): NavLeaf[] {
  if (reports.length <= REPORTS_FLAT_UP_TO) return reports;
  return REPORT_CATEGORIES.flatMap((c) => {
    const sub = c.slugs.flatMap((slug) => reports.filter((r) => r.to === `/reports/${slug}`));
    return sub.length ? [{ to: `/reports/${c.key}`, label: c.label, sub }] : [];
  });
}

/** Every screen a leaf stands for: itself, or a category's reports. */
export function leavesOf(leaf: NavLeaf): NavLeaf[] {
  return leaf.sub ?? [leaf];
}

/** Is this leaf where the page is? A category is, on its own page and on any of its reports. */
export function leafIsHere(leaf: NavLeaf, pathname: string): boolean {
  return leaf.to === pathname || (leaf.sub ?? []).some((r) => r.to === pathname);
}

/** The PHP portal screens still doing the work an area has no console screen for. */
function php(base: string, path: string, label: string): NavLeaf {
  return { to: `${base}/${path}`, label, external: true };
}

/** The four provisioning resources the brief names; screens not built yet. */
export const PROVISIONING = [
  { slug: "nas", label: "NAS" },
  { slug: "switches", label: "Switches" },
  { slug: "servers", label: "Servers" },
  { slug: "routers", label: "Routers" },
] as const;

/** How many providers the sidebar lists before "All N providers". */
export const PROVIDERS_IN_SIDEBAR = 6;

export interface ProviderEntry {
  namespace: string;
  name: string;
}

export function navGroups(
  flagEnabled: Flag,
  roles: string[],
  phpBase = "",
  providers: ProviderEntry[] = [],
  providerType: string | null = null,
  /** The namespace the console acts on; null = not known (older callers). */
  namespace: string | null = null,
): NavGroup[] {
  const staff = isStaffOnly(roles);
  // Support (the role older than staff logins) reads the portal theme, the
  // legal pages and the roster's Members page (radhotspot:read,
  // operatorcontact:read): it keeps them. billing, technical and viewer do not.
  const staffWithoutReads = staff && !holdsAny(roles, ["support"]);
  const roster: NavGroup[] = flagEnabled("roster") && !staffWithoutReads
    ? [{
        key: "roster",
        label: "Member roster",
        items: [
          { to: "/roster", label: "Members" },
          ...(canManageOrganisations(roles) && !isSponsorOnly(roles) ? [{ to: "/roster/organisations", label: "Organisations" }] : []),
        ],
      }]
    : [];
  // A sponsor is an organisation, not a provider: every other screen would only
  // answer it 403.
  if (isSponsorOnly(roles)) return roster;
  // An applicant has one job, finishing setup on the Welcome page; every other
  // screen would only answer it 403.
  if (isApplicantOnly(roles)) return [];

  // 🔑 Every area the brief names is shown, in its order, so the operator sees
  // the shape of the console. An area with no console screen yet points at the
  // PHP screen that still does the job (Billing, Devices) or at a page that
  // says so (Provisioning) -- never at nothing.
  // Providers first (the add-on brief): the operators beneath this one, when
  // there are any. Capped, then a link to the full list.
  const providerItems: NavLeaf[] = providers.slice(0, PROVIDERS_IN_SIDEBAR).map((p) => ({ to: `/providers#${p.namespace}`, label: p.name, switchTo: p.namespace }));
  if (providers.length > PROVIDERS_IN_SIDEBAR) providerItems.push({ to: "/providers", label: `All ${providers.length} providers` });
  // Owner, 2026-09-19: "Apart from listed provider, one additional submenu of
  // operator '+ Add New Provider'" -- last in the group, after the list.
  if (flagEnabled("providers.pins") && canUsePinPlans(roles)) providerItems.push({ to: PIN_PLANS_PATH, label: "Pin plans" });
  if (flagEnabled("providers.pinOrders") && canUsePinOrders(roles)) providerItems.push({ to: PIN_ORDERS_PATH, label: "Pins/Funds" });
  // Owner 2026-10-22: "Pins/Funds" -- Funds (balance, Add funds, history) live on the same screen.
  if (flagEnabled("providers.manage") && canManageSubOperators(roles)) providerItems.push({ to: ADD_PROVIDER_PATH, label: "+ Add New Provider" });

  const groups: NavGroup[] = [
    { key: "providers", label: "Providers", items: providerItems },
    {
      key: "plans",
      label: "Plans",
      items: [
        ...report(flagEnabled, "plans", "All plans"),
        ...(flagEnabled("plans.priceList") && canReadPriceList(roles) ? [{ to: PRICE_LIST_PATH, label: "Price list" }] : []),
        ...(flagEnabled("plans.components") && canReadPriceList(roles) ? [{ to: COMPONENTS_PATH, label: "Components" }] : []),
        ...(flagEnabled("plans.create") && canOpenCreatePlan(roles) ? [{ to: CREATE_PLAN_PATH, label: "Create plan" }] : []),
        ...(flagEnabled("plans.promptBox") && canPropose(roles) ? [{ to: PROMPT_PLAN_PATH, label: "Create plan from prompt", ai: true }] : []),
        // Owner 2026-10-15: View / edit plan is the console's own screen (was PHP view_plan.php).
        ...(flagEnabled("plans.create") && canClosePlans(roles) ? [{ to: ON_SALE_PATH, label: "View / edit plan" }] : []),
        ...(flagEnabled("plans.propose") && canPropose(roles) ? [{ to: PROPOSE_PATH, label: "Propose from a document", ai: true }] : []),
        ...(flagEnabled("plans.priceList") && canKeepPriceListDocs(roles) ? [{ to: PRICE_LIST_DOCS_PATH, label: "Price-list documents" }] : []),
      ],
    },
    {
      key: "promotions",
      label: "Promotions",
      items:
        flagEnabled("plans.create") && canRunPromotions(roles)
          ? [
              { to: PROMOTIONS_PATH, label: "Festive" },
              { to: PROMO_CODES_PATH, label: "Promotion codes" },
              { to: PROMO_REPORT_PATH, label: "Report" },
            ]
          : [],
    },
    {
      key: "users",
      label: "Users",
      items: [
        // Owner 2026-10-18: Customer lookup searches every index and shows plan,
        // expiry and addresses (radaccess v1.120). Flag off, or a role radaccess
        // does not let search, keeps the mobile-only report.
        ...(flagEnabled("users.lookup") && canSearchSubscribers(roles)
          ? [{ to: CUSTOMER_LOOKUP_PATH, label: "Customer lookup" }]
          : report(flagEnabled, "customer-identity", "Customer lookup")),
        ...(flagEnabled("users.import") && roles.some((r) => ["operator", "superadmin"].includes(r.toLowerCase())) ? [{ to: "/users/import", label: "Import customers" }] : []),
        ...(flagEnabled("users.create") && canCreateSubscriber(roles) ? [{ to: CREATE_SUBSCRIBER_PATH, label: "Create subscriber" }] : []),
        // Owner 2026-09-19: a PIN operator picks which subscribers get a tax
        // invoice (radaccess invoicepref:read|write, superadmin and Operator).
        ...(flagEnabled("users.invoicePref") && canUsePinPlans(roles) ? [{ to: SUBSCRIBER_PROFILES_PATH, label: "Subscriber profiles" }] : []),
        // Owner 2026-10-18: Change Device MAC sits under Users (was Devices ›
        // Change ONU MAC): any device that signs in with its MAC, not only an ONU.
        ...(flagEnabled("users.devices")
          ? canChangeDeviceMac(roles)
            ? [{ to: CHANGE_ONU_PATH, label: "Change Device MAC" }]
            : []
          : [php(phpBase, "recharge/onuchange.php", "Change Device MAC")]),
        // Owner 2026-10-15: "delete user" is Disable -- nothing is deleted (was PHP delete_user.php).
        ...(flagEnabled("billing.recharge") && canDisableSubscriber(roles) ? [{ to: DISABLE_USER_PATH, label: "Disable user" }] : []),
      ],
    },
    {
      key: "billing",
      label: "Billing",
      items: [
        // Record payment (billing lane, 2026-09-15): a UPI or cash payment
        // against an unpaid invoice or as an advance. Flagged: its invoice list
        // and customer picker need radaccess /billing routes not published yet.
        // Owner 2026-10-25: Owner, Admin and Billing staff (radaccess v1.159
        // directpayment:write); never Support, Viewer or Technical.
        ...(flagEnabled("billing.recordPayment") && canRecordPayment(roles) ? [{ to: "/billing/record-payment", label: "Record payment" }] : []),
        // Issued invoices (owner 2026-10-15): the invoices this namespace issued a
        // customer and their receipts. Same flag and grant as Record payment.
        // Owner 2026-10-25: tenantledger:read -- Owner, Admin, Billing staff,
        // and the platform ledger roles at io.ankhos.
        ...(flagEnabled("billing.recordPayment") && canReadTenantLedger(roles) ? [{ to: "/billing/issued", label: "Issued invoices" }] : []),
        // Upstream supplier (owner OB-9/14/15, 2026-10-28): what a top-level
        // provider buys from its outside bandwidth supplier, and the bills.
        // A sub-operator is refused by radaccess (409), and the screen says so.
        ...(flagEnabled("billing.upstream") && canReadUpstream(roles) ? [{ to: UPSTREAM_PATH, label: "Upstream supplier" }] : []),
        // Name on invoices (owner OB-12, 2026-10-28): whose name this
        // operator's internet invoices carry, and requests from operators
        // below it to use its name.
        ...(flagEnabled("billing.invoiceName") && canReadInvoiceName(roles) ? [{ to: INVOICE_NAME_PATH, label: "Name on invoices" }] : []),
        // Customers (owner 2026-10-23): Ankh's own customer ledger -- open an
        // operator's or a company's billing account and raise an invoice.
        // superadmin only (radaccess ankhcustomer, v1.135); the screen itself
        // tells a login outside io.ankhos to sign in there.
        // Owner 2026-10-24: at io.ankhos only -- never inside an operator.
        ...(flagEnabled("billing.ankhCustomers") && canReadAnkhCustomers(roles) && (namespace === null || namespace === PLATFORM_NAMESPACE)
          ? [{ to: ANKH_CUSTOMERS_PATH, label: "Client accounts" }]
          : []),
        // Invoices from Ankh (console lane, 2026-09-18): what AnkhOS billed this
        // provider, read-only. Shown only to a role the gateway lets read it.
        ...(flagEnabled("billing.ankhInvoices") && canReadAnkhInvoices(roles)
          ? [{ to: "/billing/invoices", label: "Invoices from Ankh" }]
          : []),
        // Recharge (owner 2026-10-11: "not any more part of our site"): with
        // billing.recharge on, the console's own page for EVERY role -- never the
        // PHP page; radaccess answers a role without subscriberrecharge with a
        // refusal that names the permission. Flag off keeps the PHP page.
        // Staff (2026-10-18): only a role that can recharge is shown it.
        ...(flagEnabled("billing.recharge")
          ? staff && !canRecharge(roles)
            ? []
            : [{ to: RECHARGE_PATH, label: "Recharge" }]
          : [php(phpBase, "recharge/recharge_form.php", "Recharge")]),
        // Owner 2026-10-15: a voucher slip printed from a real recharge receipt (was PHP print_voucher.php).
        ...(flagEnabled("billing.recharge") && canRecharge(roles) ? [{ to: PRINT_VOUCHER_PATH, label: "Print voucher" }] : []),
        // Owner 2026-10-24: subscribers who paid the operator's UPI ID; Apply recharges.
        // Owner 2026-10-25: at io.ankhos, Ankh's customers paying invoices
        // (ankhos.io/o/ankhos); superadmin only there -- no platform staff
        // role holds paymentnotice (radaccess rbac.go).
        ...(flagEnabled("billing.upiNotices") && canReadUpiNotices(roles) && (namespace !== PLATFORM_NAMESPACE || holdsAny(roles, ["superadmin"]))
          ? [{ to: UPI_NOTICES_PATH, label: "UPI payment notices" }]
          : []),
        // Owner 2026-11-02: customers asking for a wired connection; same roles as Recharge.
        ...(flagEnabled("connection.requests") && canRecharge(roles) ? [{ to: CONNECTION_REQUESTS_PATH, label: "New connection requests" }] : []),
      ],
    },
    {
      key: "devices",
      label: "Devices",
      // Owner 2026-10-18: Change Device MAC moved to Users.
      items: [],
    },
    {
      key: "reports",
      label: "Reports",
      // Flat here; navGroups folds them into REPORT_CATEGORIES on the way out,
      // after the io.ankhos filter has had its say.
      items: [
        ...report(flagEnabled, "active-sessions"),
        ...report(flagEnabled, "active-plans"),
        ...report(flagEnabled, "expiring-plans"),
        ...report(flagEnabled, "recharge-log"),
        ...report(flagEnabled, "recharge-log-by-user"),
        ...report(flagEnabled, "user-sessions"),
        ...(canReadSmsLog(roles) ? report(flagEnabled, "sms-log") : []),
        // Subscriber-management reports (owner 2026-10-24).
        ...report(flagEnabled, "expired-not-renewed"),
        ...report(flagEnabled, "failed-logins"),
        ...report(flagEnabled, "collections"),
        ...report(flagEnabled, "funds-statement"),
        ...report(flagEnabled, "new-subscribers"),
        ...report(flagEnabled, "renewals-by-plan"),
        ...report(flagEnabled, "never-used"),
        ...report(flagEnabled, "top-usage"),
        ...report(flagEnabled, "peak-hours"),
        ...report(flagEnabled, "payment-failures"),
        ...report(flagEnabled, "promo-use"),
        ...report(flagEnabled, "base-snapshot"),
        ...report(flagEnabled, "staff-activity"),
        // Platform only: the io.ankhos menu keeps it (PLATFORM_REPORTS); an
        // operator has no tenants beneath it to summarise. It carries tenants'
        // collections, so only logins that read Ankh's customers see it --
        // never onboarding or viewer staff (owner: onboarding sees no money).
        ...(namespace === PLATFORM_NAMESPACE && canReadAnkhCustomers(roles) ? report(flagEnabled, "tenant-summary") : []),
      ],
    },
    {
      key: "provisioning",
      label: "Provisioning",
      items: PROVISIONING.map((p) => ({ to: `/provisioning/${p.slug}`, label: p.label, disabled: true })),
    },
    {
      key: "themes",
      label: "Portal themes",
      items: flagEnabled("portal.appearance") && !staffWithoutReads ? [{ to: "/portal/appearance", label: "Hotspot portal" }] : [],
    },
    ...roster,
    {
      // Owner 2026-10-30: like Payment Gateway. The fund is for whoever may use
      // the wallet (radaccess pinorder); the settings are the owners' alone.
      key: "sms",
      label: "SMS Gateway",
      items: !flagEnabled("sms.gateway")
        ? []
        : namespace === PLATFORM_NAMESPACE
          ? (holdsAny(roles, ["superadmin"]) ? [{ to: SMS_FUNDS_PATH, label: "SMS funds" }] : [])
          : [
              ...(canManagePaymentGateway(roles) ? [{ to: SMS_GATEWAY_PATH, label: "Gateway accounts" }] : []),
              ...(canUsePinOrders(roles) ? [{ to: SMS_FUND_PATH, label: "SMS fund" }] : []),
            ],
    },
    {
      // Owner 2026-10-24: "In operator menu also add Payment gateway just above
      // the tools" -- the operator's own Razorpay / UPI QR accounts. At
      // io.ankhos it is the platform's own (PLATFORM_GROUPS).
      key: "payments",
      label: "Payment Gateway",
      items: flagEnabled("payments.gateway") && canManagePaymentGateway(roles) ? [{ to: PAYMENT_GATEWAY_PATH, label: "Gateway accounts" }] : [],
    },
    {
      // Owner 2026-10-21: Tools holds the checks staff run when a subscriber
      // says "I cannot sign in". User diagnose moved here from Users.
      // Owner 2026-10-23: Tools sits just above Legal & terms.
      key: "tools",
      label: "Tools",
      items: [
        ...(flagEnabled("tools.radiusTest") && canTestRadiusLogin(roles) ? [{ to: RADIUS_TEST_PATH, label: "Test RADIUS login" }] : []),
        // Owner 2026-10-17: User diagnose is the console's own screen (was PHP
        // userdiagnose.php). Flag off (radaccess older than v1.112) keeps the PHP page.
        ...(flagEnabled("users.devices")
          ? canDiagnose(roles)
            ? [{ to: DIAGNOSE_USER_PATH, label: "User diagnose" }]
            : []
          : [php(phpBase, "recharge/userdiagnose.php", "User diagnose")]),
      ],
    },
    {
      key: "legal",
      label: "Legal & terms",
      items: flagEnabled("legal.documents") && !staffWithoutReads ? [{ to: "/operator/legal", label: "Documents" }] : [],
    },
    // Settings (owner, 2026-09-14): the operator's own preferences -- logo,
    // portal and UI preferences -- an extensible area. No backend yet; the
    // page says so. The avatar menu's Preferences goes to the same page.
    {
      key: "settings",
      label: "Settings",
      items: [
        // Owner 2026-10-26: only Preferences and Staff are live; BNG and
        // TR069 stay visible, greyed.
        ...SETTINGS.map((x) => ({ to: `/settings/${x.slug}`, label: x.label, ...(x.slug === "preferences" ? {} : { disabled: true }) })),
        // Staff logins (owner, 2026-10-18): the owner invites billing,
        // support, technical and viewer staff.
        ...(flagEnabled("operator.logins") && canManageStaff(roles) ? [{ to: STAFF_PATH, label: "Staff" }] : []),
      ],
    },
  ];
  if (providerType === "CLOUD_TENANT") {
    // Billing for a cloud tenant is what it owes Ankh, nothing else: it has no
    // customers to record payments from and no PHP recharge desk.
    return groups
      .filter((g) => CLOUD_TENANT_GROUPS.includes(g.key))
      .map((g) => {
        if (g.key === "billing") return { ...g, items: g.items.filter((i) => i.to === "/billing/invoices") };
        if (g.key === "settings") return { ...g, items: g.items.filter((i) => !CLOUD_TENANT_HIDDEN_SETTINGS.includes(i.to)) };
        return g;
      })
      .filter((g) => g.items.length > 0);
  }
  if (flagEnabled("platform.tenants") && (canEditProviderIdentity(roles) || isPlatformStaff(roles)) && namespace === PLATFORM_NAMESPACE) {
    const platform: NavGroup = {
      key: "platform",
      label: "Platform",
      // Owner 2026-10-31: "Clients" is the platform's equivalent of Users.
      items: [{ to: PLATFORM_PATH, label: "Clients & invitations" }],
    };
    // Platform staff (owner 2026-10-24) see the platform desk, Ankh's ledger
    // and the SMS log -- not Plans, Legal or Settings, which are the owner's.
    const staffKeys: readonly NavGroupKey[] = ["platform", "billing", "reports"];
    return [platform, ...groups.filter((g) => PLATFORM_GROUPS.includes(g.key))]
      .filter((g) => !isPlatformStaff(roles) || staffKeys.includes(g.key))
      .map((g) => {
        if (g.key === "reports") return { ...g, items: reportCategories(g.items.filter((i) => PLATFORM_REPORTS.includes(i.to))) };
        if (g.key === "billing" && isPlatformStaff(roles)) return { ...g, items: g.items.filter((i) => i.to === ANKH_CUSTOMERS_PATH) };
        // Owner 2026-10-31: "io.ankhos never sells RADIUS plans." Price list and
        // Components stay: they hold io.ankhos's own items and their price changes.
        if (g.key === "plans")
          return {
            ...g,
            items: [
              ...(canManagePlatformPlans(roles) ? [{ to: PLATFORM_PLANS_PATH, label: "Platform plans" }] : []),
              ...g.items.filter((i) => i.to === PRICE_LIST_PATH || i.to === COMPONENTS_PATH),
            ],
          };
        return g;
      })
      .filter((g) => g.items.length > 0);
  }
  return groups.map((g) => (g.key === "reports" ? { ...g, items: reportCategories(g.items) } : g)).filter((g) => g.items.length > 0);
}

/** Settings › … (owner, 2026-09-14/15): preferences, then the BNG and TR-069 areas. */
export const SETTINGS = [
  { slug: "preferences", label: "Preferences" },
  { slug: "bng", label: "BNG" },
  { slug: "tr069", label: "TR069" },
] as const;
export const SETTINGS_PATH = "/settings/preferences";

/** Every leaf, in nav order -- what the old flat list was. */
export function navItems(flagEnabled: Flag, roles: string[], phpBase = "", providers: ProviderEntry[] = []): NavLeaf[] {
  return navGroups(flagEnabled, roles, phpBase, providers).flatMap((g) => g.items.flatMap(leavesOf));
}

/**
 * The account menu behind the avatar. The provider profile moved here from
 * the sidebar, as the brief asked: it is about the operator, not the tenant.
 */
export function accountMenu(flagEnabled: Flag, roles: string[]): NavLeaf[] {
  // Every person who signs in owns an authenticator app setting, a sponsor
  // included (owner decision 2026-09-15; radaccess grants account:* to it).
  const security = { to: SECURITY_PATH, label: "Sign-in & security" };
  if (isSponsorOnly(roles) || isApplicantOnly(roles)) return [security];
  return [
    // Staff hold no operatoridentity:read, so their Edit profile is their own
    // password only (OperatorProfile.tsx; owner 2026-10-22).
    ...(flagEnabled("operator.profile") ? [{ to: "/operator/profile", label: "Edit profile" }] : []),
    security,
    { to: SETTINGS_PATH, label: "Preferences" },
  ];
}

/** Settings › Sign-in & security: the person's own authenticator app. */
export const SECURITY_PATH = "/settings/security";

/** The group a path belongs to, so the sidebar can open it on arrival. */
export function groupOf(groups: NavGroup[], pathname: string): NavGroupKey | null {
  return groups.find((g) => g.items.some((i) => leafIsHere(i, pathname)))?.key ?? null;
}
